import { FC } from "react";
import { useResolveUploadedPath, useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import { Text, HStack, Avatar } from "@chakra-ui/react";
import {
    DataType,
    IKaTableProps,
    ITableInstance,
    Table as KaTable,
    SortingMode,
} from "ka-table";
import { Column, PagingOptions } from "ka-table/models";
import { ICellTextProps } from "ka-table/props";
import {
    AppOptionBoxItem,
    AppOptionsBox,
    KaPageSizeSelector,
} from "../../../components";
import { APPC } from "../../../config";

export const InternshipScheduleStudentList: FC<{
    table: ITableInstance;
    paging: PagingOptions;
    list: any[];
    loadingList?: boolean;
    onActionClick?: (key: string, data?: any) => void;
}> = ({
    table,
    paging,
    list,
    loadingList = false,
    onActionClick = () => {},
}) => {
    const { t } = useTrans();
    const { grant } = useAuthData();

    const columns: Column[] = [
        {
            key: "firstName",
            title: t("ent.User:name.label"),
            dataType: DataType.String,
        },
        {
            key: "email",
            title: t("ent.User:email.label"),
            dataType: DataType.String,
        },
        {
            key: "phone",
            title: t("ent.User:phone.label"),
            dataType: DataType.String,
            isSortable: false,
        },
        {
            key: "schools",
            title: t("ent.School:name.label"),
            dataType: DataType.Object,
            isSortable: false,
        },
        {
            key: "pedagogicalReferenceTrainer",
            title: t("ent.User:studentTrainer.label"),
            dataType: DataType.String,
            isSortable: false,
        },
        {
            key: "action",
            title: "",
            style: { textAlign: "right" },
            dataType: DataType.Object,
            width: 50,
            isSortable: false,
        },
    ];

    const actionOptions: AppOptionBoxItem[] = [
        {
            key: "skills",
            label: t("padm.InternshipScheduleStudentPage:text.option.skills"),
            iconName: "icl-lightbulb",
        },
        {
            key: "message",
            label: t("cmn:option.message"),
            iconName: "icl-comment-bubble-lines",
            isDisabled: grant.isCompanyDirector || grant.isSchoolDirector,
        },
        {
            key: "detail",
            label: t("cmn:option.detail"),
            iconName: "icl-eye",
        },
        {
            key: "timesheet-report",
            label: t("padm.MyInternshipPage:text.option.timesheetReport"),
            iconName: "icl-file-pdf",
        },
    ];

    const kaTableProps: IKaTableProps = {
        table: table,
        columns: columns,
        data: list || [],
        loading: {
            enabled: loadingList,
        },
        paging: paging,
        rowKeyField: "id",
        sortingMode: SortingMode.SingleRemote,
        childComponents: {
            pagingSizes: {
                content: (props) => <KaPageSizeSelector {...props} />,
            },
            cellText: {
                content: (props: ICellTextProps) => {
                    switch (props?.column?.key) {
                        case "firstName":
                            return (
                                <HStack>
                                    <Avatar
                                        size={"sm"}
                                        name={props?.rowData?.fullName}
                                        src={useResolveUploadedPath(
                                            APPC.BE.Upload.FILETYPE
                                                .FILETYPE_USER_AVATAR,
                                            props?.rowData?.imageName
                                        )}
                                        color={
                                            "var(--chakra-colors-primaryS10)"
                                        }
                                        bgColor={
                                            "var(--chakra-colors-primaryT80)"
                                        }
                                    />
                                    <Text
                                        textStyle={"mediumSm"}
                                        color={"var(--chakra-colors-black)"}
                                    >
                                        {props?.rowData?.fullName}
                                    </Text>
                                </HStack>
                            );
                        case "schools":
                            return (
                                <Text>{props?.rowData?.schools[0]?.name}</Text>
                            );
                        case "pedagogicalReferenceTrainer":
                            return (
                                <Text>
                                    {props?.rowData?.studentTrainer?.fullName}
                                </Text>
                            );
                        case "action":
                            return (
                                <HStack justify={"flex-end"} gap={3}>
                                    <AppOptionsBox
                                        options={actionOptions}
                                        data={props?.rowData}
                                        onClick={onActionClick}
                                    />
                                </HStack>
                            );
                    }
                },
            },
        },
    };

    return <KaTable {...kaTableProps} />;
};
